/**
 * Token utility functions for authentication
 */

/**
 * Check if a JWT token is valid and not expired
 * @param {string} token - The JWT token to validate
 * @returns {boolean} - True if token is valid and not expired, false otherwise
 */
export const isTokenValid = (token) => {
  if (!token) {
    return false;
  }

  try {
    // Decode the token without verification (we're just checking expiration)
    const payload = JSON.parse(atob(token.split('.')[1]));
    
    // Check if token has expired
    if (payload.exp) {
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp > currentTime;
    }
    
    // If no expiration time, consider it valid (though this shouldn't happen)
    return true;
  } catch (error) {
    // If token is malformed or can't be decoded, it's invalid
    console.error('Error decoding token:', error);
    return false;
  }
};

/**
 * Get user ID from JWT token
 * @param {string} token - The JWT token
 * @returns {string|null} - User ID if token is valid, null otherwise
 */
export const getUserIdFromToken = (token) => {
  if (!isTokenValid(token)) {
    return null;
  }

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.userId || payload.id || null;
  } catch (error) {
    console.error('Error extracting user ID from token:', error);
    return null;
  }
};

/**
 * Get user email from JWT token
 * @param {string} token - The JWT token
 * @returns {string|null} - User email if token is valid, null otherwise
 */
export const getUserEmailFromToken = (token) => {
  if (!isTokenValid(token)) {
    return null;
  }

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.email || null;
  } catch (error) {
    console.error('Error extracting user email from token:', error);
    return null;
  }
};

/**
 * Get all user data from JWT token
 * @param {string} token - The JWT token
 * @returns {object|null} - User data if token is valid, null otherwise
 */
export const getUserDataFromToken = (token) => {
  if (!isTokenValid(token)) {
    return null;
  }

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      userId: payload.userId || payload.id || null,
      // Note: Current JWT structure only includes userId
      // Email and other user data would need to be fetched separately via API
      email: payload.email || null,
      firstName: payload.firstName || null,
      lastName: payload.lastName || null
    };
  } catch (error) {
    console.error('Error extracting user data from token:', error);
    return null;
  }
};

/**
 * Check if token is expired and clean up if necessary
 * @param {string} token - The JWT token to check
 * @returns {boolean} - True if token is expired, false if still valid
 */
export const isTokenExpired = (token) => {
  return !isTokenValid(token);
};